import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { authSlice } from '../slices/auth';
import caseActionsReducer from '../slices/caseActionsSlice';
import finalReportReducer from '../slices/finalReportSlice';
import formSettingsSlice from '../slices/formSettingsSlice';
import informedIndividualsReducer from '../slices/informedIndividualsSlice';
import instantMeetingReducer from '../slices/instantMeetingSlice';
import intakeChannelDataReducer from '../slices/intakeChannelSlice';
import interviewPlanner from '../slices/interviewPlannerSlice';
import languageReducer from '../slices/languageSlice';
import meetingsReducer from '../slices/meetings';
import multiLanguageReducer from '../slices/multiLanguageSlice';
import notificationsTabReducer from '../slices/notifcationsTabSlice';
import piiRemovalReducer from '../slices/piiRemovalSlice';
import reportActionReducer from '../slices/reportActionsSlice';
import reportDataReducer from '../slices/reportDataSlice';
import reportingReducer from '../slices/reportingSlice';
import reportReducer from '../slices/reportSlice';
import rfiMessage from '../slices/rfiMessageSlice';
import securePortalReducer from '../slices/securePortalSlice';
import tenantReducer from '../slices/tenantSlices';
import tenantUserListReducer from '../slices/tenantUserListSlice';
import userProfileReducer from '../slices/userProfileSlice';
import userRolesReducer from '../slices/userRolesSlice';

import { restAPI } from '~/services/api/rest-api';

export const store = configureStore({
  reducer: {
    [restAPI.reducerPath]: restAPI.reducer,
    auth: authSlice.reducer,
    finalReport: finalReportReducer,
    reports: reportReducer,
    reporting: reportingReducer,
    securePortal: securePortalReducer,
    informedIndividuals: informedIndividualsReducer,
    piiRemoval: piiRemovalReducer,
    notificationsTab: notificationsTabReducer,
    userProfile: userProfileReducer,
    meetings: meetingsReducer,
    instantMeeting: instantMeetingReducer,
    tenantUserList: tenantUserListReducer,
    userRoles: userRolesReducer,
    languages: languageReducer,
    reportData: reportDataReducer,
    intakeChannelData: intakeChannelDataReducer,
    caseActions: caseActionsReducer,
    reportAction: reportActionReducer,
    interviewPlanner,
    tenant: tenantReducer,
    multiLanguage: multiLanguageReducer,
    formSettings: formSettingsSlice,
    rfiMessage,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(restAPI.middleware),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useTypedDispatch: () => AppDispatch = useDispatch;
